import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Package,
  AlertCircle,
  XCircle,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetStockSummaryQuery,
  useGetLowStockProductsQuery,
  type ProductStockAlert,
  type StockAlertLevel,
} from "@/store";

const ALERT_CONFIG: Record<
  StockAlertLevel,
  { label: string; color: string; icon: React.ReactNode; bgColor: string }
> = {
  OUT_OF_STOCK: {
    label: "Out of Stock",
    color: "text-red-600 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/20",
    icon: <XCircle className="h-3 w-3" />,
  },
  CRITICAL: {
    label: "Critical",
    color: "text-orange-600 dark:text-orange-400",
    bgColor: "bg-orange-100 dark:bg-orange-900/20",
    icon: <AlertCircle className="h-3 w-3" />,
  },
  LOW: {
    label: "Low",
    color: "text-yellow-600 dark:text-yellow-400",
    bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
    icon: <AlertTriangle className="h-3 w-3" />,
  },
  NORMAL: {
    label: "Normal",
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-100 dark:bg-green-900/20",
    icon: <Package className="h-3 w-3" />,
  },
};

function StockAlertBadge({ level }: { level: StockAlertLevel }) {
  const config = ALERT_CONFIG[level];
  return (
    <Badge className={`${config.bgColor} ${config.color} border-0 gap-1`}>
      {config.icon}
      {config.label}
    </Badge>
  );
}

function ProductAlertItem({ product }: { product: ProductStockAlert }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b last:border-0">
      <div className="h-10 w-10 rounded-md overflow-hidden bg-muted flex-shrink-0">
        <img
          src={product.image}
          alt={product.name}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{product.name}</p>
        <p className="text-xs text-muted-foreground">
          {product.stockQuantity} in stock
        </p>
      </div>
      <StockAlertBadge level={product.alertLevel} />
    </div>
  );
}

interface LowStockAlertProps {
  maxItems?: number;
  showSummary?: boolean;
}

export function LowStockAlert({ maxItems = 5, showSummary = true }: LowStockAlertProps) {
  const {
    data: summaryData,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useGetStockSummaryQuery();

  const {
    data: alertsData,
    isLoading: alertsLoading,
    refetch: refetchAlerts,
  } = useGetLowStockProductsQuery({ limit: maxItems });

  const summary = summaryData?.data;
  const alerts = alertsData?.data || [];

  const handleRefresh = () => {
    refetchSummary();
    refetchAlerts();
  };

  const totalAlerts = (summary?.outOfStock || 0) + (summary?.criticalStock || 0) + (summary?.lowStock || 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Stock Alerts
          </CardTitle>
          <CardDescription>
            Products requiring attention
          </CardDescription>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          disabled={summaryLoading || alertsLoading}
        >
          <RefreshCw className={`h-4 w-4 ${(summaryLoading || alertsLoading) ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        {showSummary && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            {summaryLoading ? (
              <>
                <Skeleton className="h-16 rounded-lg" />
                <Skeleton className="h-16 rounded-lg" />
                <Skeleton className="h-16 rounded-lg" />
              </>
            ) : (
              <>
                <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {summary?.outOfStock || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Out of Stock</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {summary?.criticalStock || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Critical</p>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {summary?.lowStock || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Low Stock</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Alert List */}
        {alertsLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-md" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-32 mb-1" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-5 w-16" />
              </div>
            ))}
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-6">
            <Package className="h-10 w-10 mx-auto text-green-500 mb-2" />
            <p className="text-sm text-muted-foreground">
              All products have healthy stock levels
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {alerts.map((product) => (
              <ProductAlertItem key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* View All Link */}
        {totalAlerts > 0 && (
          <div className="mt-4 pt-4 border-t">
            <Link to="/admin/products?stockFilter=low">
              <Button variant="outline" className="w-full gap-2">
                View All Stock Alerts ({totalAlerts})
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compact version for use in header or sidebar
 */
export function LowStockIndicator() {
  const { data: summaryData, isLoading } = useGetStockSummaryQuery();
  const summary = summaryData?.data;

  const totalAlerts = (summary?.outOfStock || 0) + (summary?.criticalStock || 0);

  if (isLoading || totalAlerts === 0) {
    return null;
  }

  return (
    <Link to="/admin/products?stockFilter=low">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/20 rounded-full text-amber-700 dark:text-amber-400 text-sm font-medium hover:bg-amber-200 dark:hover:bg-amber-900/30 transition-colors">
        <AlertTriangle className="h-4 w-4" />
        <span>{totalAlerts} stock alert{totalAlerts !== 1 ? "s" : ""}</span>
      </div>
    </Link>
  );
}
